import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  MuiThemeProvider,
  createMuiTheme,
} from "@material-ui/core";
import {
  getBod2Summary,
  getBod2Gap,
  getBod3Summary,
  getBod3Gap,
} from "redux/actions/dashboard";
import "./style.css";
import { makeStyles, withStyles } from "@material-ui/styles";
import GapDetailModal from "components/Modal/src/gap_detail";
import { getProject } from "redux/actions/proyek";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

const useStyles = makeStyles({
  notchedOutline: {},
  focused: {
    "& $notchedOutline": {
      borderColor: "#00abbd !important",
    },
  },
});

const selectTheme = createMuiTheme({
  palette: {
    primary: {
      main: "#00abbd",
    },
  },
});

export default function BarCharts() {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { bod2_summary, bod2_gap, bod3_summary, bod3_gap } = useSelector(
    (state) => state.dashboard,
  );

  const { proyek_list } = useSelector((state) => state.proyek);

  const [bod2Type, setBod2Type] = useState("summary");
  const [bod3Type, setBod3Type] = useState("summary");

  const [bod2ProjectId, setBod2ProjectId] = useState("");
  const [bod3ProjectId, setBod3ProjectId] = useState("");

  const [detailModal, setDetailModal] = useState({
    open: false,
    categoryId: null,
    categoryName: "",
    bodLevel: null,
    projectId: null,
    projectName: "",
  });

  useEffect(() => {
    dispatch(getProject("/1/100/*"));

    dispatch(getBod2Summary());
    dispatch(getBod2Gap());
    dispatch(getBod3Summary());
    dispatch(getBod3Gap());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getBod2Summary(bod2ProjectId || null));
    dispatch(getBod2Gap(bod2ProjectId || null));
  }, [bod2ProjectId]);

  useEffect(() => {
    dispatch(getBod3Summary(bod3ProjectId || null));
    dispatch(getBod3Gap(bod3ProjectId || null));
  }, [bod3ProjectId]);

  const RadioButton = withStyles({
    root: {
      color: "#00abbd",
      "&$checked": { color: "#00abbd" },
    },
    checked: {},
  })((props) => <Radio color="default" {...props} />);

  const transformChartData = (data, type) => {
    if (!data || !data.data) return null;

    return {
      labels: data.data.map((item) => item.category),
      datasets: [
        {
          label: type === "gap" ? "Count" : "Percentage",
          data: data.data.map((item) =>
            type === "gap" ? item.count : item.percent,
          ),
          backgroundColor: "#00abbd",
          borderWidth: 1,
        },
      ],
    };
  };

  const getProjectName = (projectId) => {
    if (!projectId) return "";
    return proyek_list.find((p) => p.id === projectId)?.name || "";
  };

  const getChartHeight = (data) => {
    if (!data || !data.data) return 400;
    const categoryCount = data.data.length;
    return Math.min(Math.max(categoryCount * 40, 75), 400);
  };

  const getMaxValue = (data, type) => {
    if (!data || !data.data || data.data.length === 0) return 100;

    if (type === "gap") {
      const maxCount = Math.max(...data.data.map((item) => item.count));
      return Math.ceil(maxCount * 1.2);
    } else {
      const maxPercent = Math.max(...data.data.map((item) => item.percent));
      return Math.ceil((maxPercent * 1.2) / 10) * 10;
    }
  };

  const makeClickHandler =
    (bodLevel, type, sourceData, projectId, projectName) => (_, elements) => {
      if (type !== "gap") return;
      if (!elements || elements.length === 0) return;

      const index = elements[0].index;
      const clickedItem = sourceData?.data?.[index];
      if (!clickedItem) return;

      setDetailModal({
        open: true,
        categoryId: clickedItem.categoryId,
        categoryName: clickedItem.category,
        bodLevel,
        projectId: projectId || null,
        projectName: projectName || "",
      });
    };

  const getChartOptions = (
    maxValue,
    type,
    sourceData,
    bodLevel,
    projectId,
    projectName,
  ) => ({
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    onHover: (event, elements) => {
      if (type === "gap") {
        event.native.target.style.cursor =
          elements.length > 0 ? "pointer" : "default";
      }
    },
    onClick: makeClickHandler(
      bodLevel,
      type,
      sourceData,
      projectId,
      projectName,
    ),
    plugins: {
      legend: { display: false },
      title: { display: false },
      datalabels: {
        anchor: "end",
        align: "end",
        formatter: (value) => (type === "gap" ? value + " orang" : value + "%"),
        font: { weight: "bold", size: 12 },
        color: "#333",
      },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label,
          label: (item) =>
            type === "gap" ? `${item.raw} orang` : `${item.raw}%`,
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: maxValue,
        ticks: {
          stepSize: 25,
          callback: (value) => (type === "gap" ? value : value + "%"),
        },
      },
    },
  });

  const bod2ChartData =
    bod2Type === "gap"
      ? transformChartData(bod2_gap, "gap")
      : transformChartData(bod2_summary, "summary");

  const bod3ChartData =
    bod3Type === "gap"
      ? transformChartData(bod3_gap, "gap")
      : transformChartData(bod3_summary, "summary");

  const bod2Height =
    bod2Type === "gap"
      ? getChartHeight(bod2_gap)
      : getChartHeight(bod2_summary);
  const bod3Height =
    bod3Type === "gap"
      ? getChartHeight(bod3_gap)
      : getChartHeight(bod3_summary);

  const bod2MaxValue =
    bod2Type === "gap"
      ? getMaxValue(bod2_gap, "gap")
      : getMaxValue(bod2_summary, "summary");
  const bod3MaxValue =
    bod3Type === "gap"
      ? getMaxValue(bod3_gap, "gap")
      : getMaxValue(bod3_summary, "summary");

  const ProjectDropdown = ({ value, onChange }) => (
    <MuiThemeProvider theme={selectTheme}>
      <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
        <InputLabel id="project-label" shrink={proyek_list?.length > 0}>
          Project
        </InputLabel>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
          color="primary"
          labelId="project-label"
          label="Project"
          notched={proyek_list.length > 0}
          renderValue={(selected) => {
            if (proyek_list.length === 0) return "";
            if (selected === "") {
              return "Semua Project";
            }
            const project = proyek_list.find((p) => p.id === selected);
            return project ? project.name : "Semua Project";
          }}
        >
          <MenuItem value="">Semua Project</MenuItem>
          {proyek_list.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </MuiThemeProvider>
  );
  return (
    <div className="chart-comparison-wrapper">
      {/* BOD-2 */}
      <div className="chart-section">
        <div className="chart-header">
          <h4>Kompetensi BOD-2</h4>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <ProjectDropdown value={bod2ProjectId} onChange={setBod2ProjectId} />
          <RadioGroup
            row
            value={bod2Type}
            onChange={(e) => setBod2Type(e.target.value)}
          >
            <FormControlLabel
              value="summary"
              control={<RadioButton />}
              label="Summary"
            />
            <FormControlLabel
              value="gap"
              control={<RadioButton />}
              label="Gap"
            />
          </RadioGroup>
        </div>
        <div className="chart-container" style={{ height: `${bod2Height}px` }}>
          {bod2ChartData && bod2ChartData.labels.length > 0 ? (
            <Bar
              data={bod2ChartData}
              options={getChartOptions(
                bod2MaxValue,
                bod2Type,
                bod2_gap,
                2,
                bod2ProjectId,
                getProjectName(bod2ProjectId),
              )}
            />
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
              Tidak ada data untuk project ini
            </div>
          )}
        </div>
      </div>

      {/* BOD-3 */}
      <div className="chart-section">
        <div className="chart-header">
          <h4>Kompetensi BOD-3</h4>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <ProjectDropdown value={bod3ProjectId} onChange={setBod3ProjectId} />
          <RadioGroup
            row
            value={bod3Type}
            onChange={(e) => setBod3Type(e.target.value)}
          >
            <FormControlLabel
              value="summary"
              control={<RadioButton />}
              label="Summary"
            />
            <FormControlLabel
              value="gap"
              control={<RadioButton />}
              label="Gap"
            />
          </RadioGroup>
        </div>
        <div className="chart-container" style={{ height: `${bod3Height}px` }}>
          {bod3ChartData && bod3ChartData.labels.length > 0 ? (
            <Bar
              data={bod3ChartData}
              options={getChartOptions(
                bod3MaxValue,
                bod3Type,
                bod3_gap,
                3,
                bod3ProjectId,
                getProjectName(bod3ProjectId),
              )}
            />
          ) : (
            <div style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
              Tidak ada data untuk project ini
            </div>
          )}
        </div>
      </div>

      <GapDetailModal
        open={detailModal.open}
        onClose={() => setDetailModal((prev) => ({ ...prev, open: false }))}
        categoryId={detailModal.categoryId}
        categoryName={detailModal.categoryName}
        bodLevel={detailModal.bodLevel}
        projectId={detailModal.projectId}
        projectName={detailModal.projectName}
      />
    </div>
  );
}
